import {
  FONT_FACES_STYLE_ID,
  FULL_STYLESHEET_ANCHOR_ID,
  getConfigFontFaceSources,
  getFontVariablesCss,
  SSR_FONTS_STYLE_ID
} from '@shared/domain/utils/css'
import { findFontFace, formatFontWeight, getFontFaceRule } from '@shared/domain/utils/fonts'
import { cdnPath, getTailwindStylesheetUrl } from '@shared/domain/utils/remoteUrl'

const addScript = (src) => {
  return new Promise((resolve, reject) => {
    let shouldAppend = false
    let el = document.querySelector('script[src="' + src + '"]')
    if (!el) {
      el = document.createElement('script')
      el.type = 'text/javascript'
      el.async = true
      el.src = src
      shouldAppend = true
    } else if (el.hasAttribute('data-loaded')) {
      resolve(el)
      return
    }

    el.addEventListener('error', reject)
    el.addEventListener('abort', reject)
    el.addEventListener('load', () => {
      el.setAttribute('data-loaded', true)
      resolve(el)
    })

    if (shouldAppend) {
      document.head.appendChild(el)
    }
  })
}

const insertFullStylesheetAtAnchor = (href) => {
  const anchor = document.getElementById(FULL_STYLESHEET_ANCHOR_ID)
  if (!anchor) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.insertBefore(link, anchor)
}

const initScrolledState = () => {
  const html = document.documentElement

  const updateScrolledState = () => {
    const scrolled = window.scrollY > 10
    const hasAttr = html.hasAttribute('data-scrolled')
    if (scrolled && !hasAttr) {
      html.setAttribute('data-scrolled', '')
    } else if (!scrolled && hasAttr) {
      html.removeAttribute('data-scrolled')
    }
  }

  updateScrolledState()
  window.addEventListener('scroll', updateScrolledState, { passive: true })
  window.addEventListener('pageshow', updateScrolledState)
  requestAnimationFrame(updateScrolledState)
}

const injectStyle = (id, content) => {
  if (!content || document.getElementById(id)) return

  const style = document.createElement('style')
  style.id = id
  style.textContent = content
  document.head.appendChild(style)
}

const injectFontVariablesFallback = () =>
  injectStyle(SSR_FONTS_STYLE_ID, getFontVariablesCss(window.mixConfig))

const ITALIC_STYLE = 'italic'
const NORMAL_STYLE = 'normal'

const declareFontFaces = (sources) =>
  injectStyle(FONT_FACES_STYLE_ID, sources.map(getFontFaceRule).join('\n'))

const getPrimaryFontFamily = (fontFamily) =>
  fontFamily
    .split(',')[0]
    .trim()
    .replace(/^["']|["']$/g, '')

const checkHasOwnText = (element) =>
  [...element.childNodes].some(
    (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
  )

const findRenderedFontFaces = (sources) => {
  const used = new Set()
  document.body.querySelectorAll('*').forEach((element) => {
    if (!checkHasOwnText(element)) return
    const style = getComputedStyle(element)
    const fontStyle = style.fontStyle === ITALIC_STYLE ? ITALIC_STYLE : NORMAL_STYLE
    const face = findFontFace(
      sources,
      getPrimaryFontFamily(style.fontFamily),
      Number(style.fontWeight),
      fontStyle
    )
    if (face) used.add(face)
  })
  return [...used]
}

const toFontFace = (source) =>
  new FontFace(source.family, `url(${source.url}) format('${source.format}')`, {
    style: source.style,
    weight: formatFontWeight(source.weight),
    display: 'swap'
  })

const loadFontFaces = () => {
  const sources = getConfigFontFaceSources(window.mixConfig)
  const rendered = findRenderedFontFaces(sources)
  const later = sources.filter((source) => !rendered.includes(source))
  const fontFaces = rendered.map(toFontFace)

  Promise.all(fontFaces.map((fontFace) => fontFace.load()))
    .then(() => {
      fontFaces.forEach((fontFace) => document.fonts.add(fontFace))
      declareFontFaces(later)
    })
    .catch(() => declareFontFaces(sources))
}

const checkSupportsPaintTiming = () =>
  !!window.PerformanceObserver?.supportedEntryTypes?.includes('paint')

const COMPLETE_READY_STATE = 'complete'

const whenFirstPainted = () =>
  new Promise((resolve) => {
    if (!checkSupportsPaintTiming()) {
      resolve()
      return
    }
    new PerformanceObserver((_, observer) => {
      observer.disconnect()
      resolve()
    }).observe({ type: 'paint', buffered: true })
  })

const whenPageLoaded = () =>
  new Promise((resolve) => {
    if (document.readyState === COMPLETE_READY_STATE) {
      resolve()
      return
    }
    window.addEventListener('load', resolve, { once: true })
  })

const afterNextPaint = () =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))

const loadFontFacesAfterPageLoad = () =>
  Promise.all([whenFirstPainted(), whenPageLoaded()]).then(afterNextPaint).then(loadFontFaces)

const initDelayedHydration = () => {
  const interactionEvents = ['mousemove', 'scroll', 'keydown', 'click', 'touchstart', 'wheel']
  let alreadyAdded = false

  const loadMainBundle = () => {
    if (alreadyAdded) {
      return
    }
    alreadyAdded = true
    injectFontVariablesFallback()
    insertFullStylesheetAtAnchor(getTailwindStylesheetUrl())
    interactionEvents.forEach((event) => window.removeEventListener(event, loadMainBundle))
    addScript(cdnPath('/shell/client/main.js')).catch((err) =>
      /* eslint-disable-next-line no-console */
      console.error('Hydration: failed to load', err)
    )
  }

  interactionEvents.forEach((event) =>
    window.addEventListener(event, loadMainBundle, { once: true })
  )
  setTimeout(loadMainBundle, window?._delayedHydration || 6000)
}

initScrolledState()
loadFontFacesAfterPageLoad()
initDelayedHydration()
